import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import searchIcon from "../../assets/icons/Search.png";
import filterIcon from "../../assets/icons/filter.png";
import crateIcon from "../../assets/icons/crate.png";
import avatar from "../../assets/icons/user-avatar2.png";
import homeIcon from "../../assets/icons/home.png";
import remixerIcon from "../../assets/icons/remixers.svg";

import FilterModal from "../Common/AdvanceFilterModal";
import CrateSidebar from "../Layout/CrateSidebar";
import AvatarDropdown from "../Layout/AvatarDropdown";
import SubmitTicketModal from "../Dashboard/SubmitTicketModal";
import { useLanguage } from "../../contexts/LanguageContext";
import { logout, signoutUser } from "../../store";

import SupremeLogo from "../Common/SupremeLogo";

const Navbar = ({ toggleSidebar }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCrateOpen, setIsCrateOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showHelpdeskModal, setShowHelpdeskModal] = useState(false);

  const avatarRef = useRef(null);
  const dropdownRef = useRef(null);
  const crateRef = useRef(null);

  const crateItems = useSelector((state) => state.crate.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useLanguage();
  const [showLogo, setShowLogo] = useState(true);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        avatarRef.current &&
        !avatarRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }

      if (
        crateRef.current &&
        !crateRef.current.contains(e.target) &&
        !e.target.closest("#crate-icon")
      ) {
        setIsCrateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 0);

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navItems = [
    {
      label: translations?.DashboardNav?.home || "Home",
      path: "/subscription",
    },
    {
      label: translations?.DashboardNav?.payment || "Payment Form",
      path: "/membership",
    },
    {
      label: translations?.DashboardNav?.helpdesk || "Helpdesk",
      path: "/helpdesk",
    },
    {
      label: translations?.DashboardNav?.member || "Member Profile",
      path: "/dashboard",
    },
  ];

  return (
    <header
      className={`fixed top-0 z-50 h-[120px] sm:h-[80px] transition-transform duration-300 text-white w-full left-0 px-1 lg:px-6
       ${
         scrolled
           ? "bg-[#080B12]/10 backdrop-blur-md"
           : "bg-[#080B12]/10 backdrop-blur-sm"
       }`}
    >
      <div className="max-w-screen-xl mx-auto w-full">
        <div className="max-w-screen-xl mx-auto w-full flex items-center justify-between">
          <button
            className="lg:hidden p-6"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  isMobileMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>

          {/* Logo Section - Common across dashboard pages */}
          {showLogo && !isCrateOpen && (
            <Link
              to="/home"
              className="absolute left-1/2 transform -translate-x-1/2 top-[8px] flex items-center bg-transparent w-fit h-[60.64px] gap-[4.86px] px-[30px] py-[12px] cursor-pointer hover:opacity-80 transition-opacity z-50"
            >
              <div translate="no" className="flex items-center justify-center w-[120px] h-[50px] sm:w-[180px] sm:h-[60px] md:w-[160.87px] md:h-[68.27px] sm:justify-start order-2 sm:order-none mt-12 sm:mt-0 notranslate">
                <SupremeLogo />
              </div>
            </Link>
          )}

          <div className="w-full h-[165px] hidden lg:flex items-center justify-center">
            <div className="absolute left-1/2 transform -translate-x-1/2 hidden lg:block">
              <div className="flex items-center bg-[#1E1E1E] w-fit rounded-[8px] p-1 gap-[30px] mx-auto mt-[30px]">
                {/* Home */}
                {/* Payment form */}
                {/* Helpdesk */}
                {/* Member profile */}
                {/* (Put your nav menu buttons here directly) */}

                {/* Home Button */}
                <button
                  onClick={() => navigate("/subscription")}
                  className={`w-[113px] h-[43px] px-[8px] py-[12px] flex items-center gap-[6px] justify-center rounded-[9px] text-white
                      ${
                        location.pathname === "/subscription"
                          ? "bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0]"
                          : "bg-[#1E1E1E]"
                      }`}
                >
                  <svg
                    width="18"
                    height="17"
                    viewBox="0 0 18 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12.9925V10.7425M6.765 1.63L2.7225 4.78C2.0475 5.305 1.5 6.4225 1.5 7.27V12.8275C1.5 14.5675 2.9175 15.9925 4.6575 15.9925H13.3425C15.0825 15.9925 16.5 14.5675 16.5 12.835V7.375C16.5 6.4675 15.8925 5.305 15.15 4.7875L10.515 1.54C9.465 0.805 7.7775 0.8425 6.765 1.63Z"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{translations?.DashboardNav?.home || "Home"}</span>
                </button>

                {/* Payment Form */}
                <button
                  onClick={() => navigate("/membership")}
                  className={`w-[178px] h-[43px] px-[8px] py-[12px] flex items-center gap-[10px] justify-center rounded-[9px] text-white
                      ${
                        location.pathname === "/membership"
                          ? "bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0]"
                          : "bg-[#1E1E1E]"
                      }`}
                >
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.06367 13.145V14.6675C8.06367 15.9575 6.86367 17 5.38617 17C3.90867 17 2.70117 15.9575 2.70117 14.6675V13.145C2.70117 14.435 3.90117 15.35 5.38617 15.35C6.86367 15.35 8.06367 14.4275 8.06367 13.145Z"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.06336 11.0825C8.06336 11.4575 7.95836 11.8025 7.77836 12.1025C7.33586 12.83 6.42836 13.2875 5.37836 13.2875C4.32836 13.2875 3.42086 12.8225 2.97836 12.1025C2.79836 11.8025 2.69336 11.4575 2.69336 11.0825C2.69336 10.4375 2.99336 9.86001 3.47336 9.44001C3.96086 9.01251 4.62836 8.75751 5.37086 8.75751C6.11336 8.75751 6.78086 9.02001 7.26836 9.44001C7.76336 9.85251 8.06336 10.4375 8.06336 11.0825Z"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.06367 11.0825V13.145C8.06367 14.435 6.86367 15.35 5.38617 15.35C3.90867 15.35 2.70117 14.4275 2.70117 13.145V11.0825C2.70117 9.79249 3.90117 8.74999 5.38617 8.74999C6.12867 8.74999 6.79617 9.01249 7.28367 9.43249C7.76367 9.85249 8.06367 10.4375 8.06367 11.0825ZM16.4997 8.72749V10.2725C16.4997 10.685 16.1697 11.0225 15.7497 11.0375H14.2797C13.4697 11.0375 12.7272 10.445 12.6597 9.63499C12.6147 9.16249 12.7947 8.71999 13.1097 8.41249C13.3872 8.12749 13.7697 7.96249 14.1897 7.96249H15.7497C16.1697 7.97749 16.4997 8.31499 16.4997 8.72749Z"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1.5 8.375V6.875C1.5 4.835 2.73 3.41 4.6425 3.17C4.8375 3.14 5.04 3.125 5.25 3.125H12C12.195 3.125 12.3825 3.1325 12.5625 3.1625C14.4975 3.3875 15.75 4.82 15.75 6.875V7.9625H14.19C13.77 7.9625 13.3875 8.1275 13.11 8.4125C12.795 8.72 12.615 9.1625 12.66 9.635C12.7275 10.445 13.47 11.0375 14.28 11.0375H15.75V12.125C15.75 14.375 14.25 15.875 12 15.875H10.125"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {/* <span>Payment form</span> */}
                  <span>
                    {translations?.DashboardNav?.payment || "Payment Form"}
                  </span>
                </button>

                {/* Helpdesk */}
                <button
                  onClick={() => navigate("/helpdesk")}
                  className={`w-[140px] h-[43px] px-[10px] py-[12px] flex items-center gap-[6px] justify-center rounded-[9px] text-white
                      ${
                        location.pathname === "/helpdesk"
                          ? "bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0]"
                          : "bg-[#1E1E1E]"
                      }`}
                >
                  <svg
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.75 14.3225H9.75L6.4125 16.5425C6.29987 16.6176 6.16896 16.6607 6.03375 16.6672C5.89854 16.6737 5.7641 16.6434 5.64476 16.5795C5.52543 16.5156 5.42568 16.4205 5.35616 16.3044C5.28663 16.1882 5.24994 16.0554 5.25 15.92V14.3225C3 14.3225 1.5 12.8225 1.5 10.5725V6.07251C1.5 3.82251 3 2.32251 5.25 2.32251H12.75C15 2.32251 16.5 3.82251 16.5 6.07251V10.5725C16.5 12.8225 15 14.3225 12.75 14.3225Z"
                      stroke="white"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.00086 9.01999V8.86249C9.00086 8.35249 9.31586 8.08249 9.63086 7.86499C9.93836 7.65499 10.2459 7.38499 10.2459 6.88999C10.2459 6.19999 9.69086 5.64499 9.00086 5.64499C8.31086 5.64499 7.75586 6.19999 7.75586 6.88999M8.99711 10.8125H9.00461"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    {translations?.DashboardNav?.helpdesk || "Helpdesk"}
                  </span>
                </button>

                {/* Member Profile */}
                <button
                  onClick={() => navigate("/dashboard")}
                  className={`w-[184px] h-[43px] px-[20px] py-[12px] flex items-center gap-[6px] justify-center rounded-[9px] text-white
                      ${
                        location.pathname === "/dashboard"
                          ? "bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0]"
                          : "bg-[#1E1E1E]"
                      }`}
                >
                  <img
                    src={remixerIcon}
                    alt="Remixers"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span>
                    {translations?.DashboardNav?.member || "Member profile"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-7">
            <div
              id="crate-icon"
              onClick={() => setIsCrateOpen(true)}
              className="relative cursor-pointer"
            >
              {/* SVG Crate Icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[20px] h-[20px] sm:w-[22px] sm:h-[22px] lg:w-[24px] lg:h-[24px]"
              >
                <path
                  d="M21.6698 14.3L21.2698 19.3C21.1198 20.83 20.9998 22 18.2898 22H5.70977C2.99977 22 2.87977 20.83 2.72977 19.3L2.32977 14.3C2.24977 13.47 2.50977 12.7 2.97977 12.11L2.99977 12.09C3.54977 11.42 4.37977 11 5.30977 11H18.6898C19.6198 11 20.4398 11.42 20.9798 12.07C20.9898 12.08 20.9998 12.09 20.9998 12.1C21.4898 12.69 21.7598 13.46 21.6698 14.3Z"
                  stroke="white"
                  strokeMiterlimit="10"
                />
                <path
                  d="M3.5 11.4298V6.27978C3.5 2.87978 4.35 2.02979 7.75 2.02979H9.02C10.29 2.02979 10.58 2.40979 11.06 3.04978L12.33 4.74978C12.65 5.16978 12.84 5.42978 13.69 5.42978H16.24C19.64 5.42978 20.49 6.27978 20.49 9.67978V11.4698"
                  stroke="white"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.43 16.9998H14.57"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="19"
                  cy="5"
                  r="3.5"
                  fill="#FF0000"
                  stroke="#0A0A0A"
                />
              </svg>
              <span className="absolute -top-0.5 sm:-top-1 -right-0.5 sm:-right-1 bg-[#FF000D] text-white text-[8px] sm:text-[10px] px-[3px] sm:px-[5px] rounded-full leading-none border border-black min-w-[14px] sm:min-w-[16px] h-[14px] sm:h-[16px] flex items-center justify-center">
                {crateItems?.length}
              </span>
            </div>

            {/* <div className="relative" ref={avatarRef}>
              <img
                src={avatar}
                alt="Avatar"
                // className="w-9 h-9 rounded-full cursor-pointer"
                className="w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] lg:w-[44px] lg:h-[44px] m-[4px] rounded-full object-cover  cursor-pointer"
                onClick={() => setShowDropdown((prev) => !prev)}
              />
              {showDropdown && (
                <div ref={dropdownRef} className="absolute right-0 mt-2">
                  <AvatarDropdown onLogout={() => dispatch(logout())} />
                </div>
              )}
            </div> */}

            <div className="relative" ref={avatarRef}>
              <div
                className="w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] lg:w-[44px] lg:h-[44px] rounded-full overflow-hidden flex items-center justify-center hover:bg-[#1E1E1E] transition-all duration-300"
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <img
                  src={avatar}
                  alt="Profile"
                  className="w-[28px] h-[28px] sm:w-[34px] sm:h-[34px] lg:w-[38px] lg:h-[38px] m-[2px] lg:m-[4px] rounded-full object-cover cursor-pointer"
                />
              </div>
              {showDropdown && (
                <div ref={dropdownRef}>
                  <AvatarDropdown
                    onLogout={async () => {
                      await dispatch(signoutUser());
                      dispatch(logout());
                    }}
                    onClose={() => setShowDropdown(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-12 px-0 py-0 flex flex-col ">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className="text-center px-4 py-2 bg-[#1A1A1D]"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <CrateSidebar
          ref={crateRef}
          isOpen={isCrateOpen}
          onClose={() => setIsCrateOpen(false)}
        />
      </div>
    </header>
  );
};

export default Navbar;
